/**
 * useOrganizations Hook
 * Provides access to organization data with CRUD operations
 */

import { useAtom } from "jotai";
import { useMemo, useCallback, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { organizationsQueryAtom } from "@/store/adminDataStore";
import { adminAPI } from "@/services";
import type {
  IOrganization,
  IOrganizationInput,
  IOrganizationActivationInput,
} from "@/services";
import { invalidateWithTimeout } from "@/utils/queryUtils";

export interface UseOrganizationsReturn {
  /** All organizations */
  organizations: IOrganization[];
  /** Active organizations only */
  activeOrganizations: IOrganization[];
  /** Inactive organizations only */
  inactiveOrganizations: IOrganization[];
  /** Loading state */
  isLoading: boolean;
  /** Error state */
  error: Error | null;
  /** Refetch function */
  refetch: () => void;
  /** Find organization by ID */
  findById: (id: number) => IOrganization | undefined;
  /** Find organization by tenant ID */
  findByTenantId: (tenantId: string) => IOrganization | undefined;
  /** Create or update organization */
  saveOrganization: (data: IOrganizationInput) => Promise<boolean>;
  /** Activate an organization */
  activateOrganization: (id: number) => Promise<boolean>;
  /** Deactivate an organization */
  deactivateOrganization: (id: number) => Promise<boolean>;
  /** Saving state */
  isSaving: boolean;
  /** Save error */
  saveError: Error | null;
  /** Activation toggle state */
  isTogglingActivation: boolean;
  /** Activation toggle error */
  activationError: Error | null;
}

export function useOrganizations(): UseOrganizationsReturn {
  const [query] = useAtom(organizationsQueryAtom);
  const queryClient = useQueryClient();
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<Error | null>(null);
  const [isTogglingActivation, setIsTogglingActivation] = useState(false);
  const [activationError, setActivationError] = useState<Error | null>(null);

  // All organizations
  const organizations = useMemo(() => query.data ?? [], [query.data]);

  // Active organizations
  const activeOrganizations = useMemo(
    () => organizations.filter((org) => org.is_active),
    [organizations],
  );

  // Inactive organizations
  const inactiveOrganizations = useMemo(
    () => organizations.filter((org) => !org.is_active),
    [organizations],
  );

  // Find by ID
  const findById = useCallback(
    (id: number) => organizations.find((org) => org.id === id),
    [organizations],
  );

  // Find by tenant ID
  const findByTenantId = useCallback(
    (tenantId: string) =>
      organizations.find(
        (org) => org.tenant_id.toLowerCase() === tenantId.toLowerCase(),
      ),
    [organizations],
  );

  // Save organization mutation
  const saveOrganization = useCallback(
    async (data: IOrganizationInput): Promise<boolean> => {
      setIsSaving(true);
      setSaveError(null);

      try {
        await adminAPI.saveOrganization(data);
        await invalidateWithTimeout(queryClient, ["organizations"]);
        return true;
      } catch (error) {
        setSaveError(error as Error);
        return false;
      } finally {
        setIsSaving(false);
      }
    },
    [queryClient],
  );

  // Toggle activation helper
  const toggleActivation = useCallback(
    async (data: IOrganizationActivationInput): Promise<boolean> => {
      setIsTogglingActivation(true);
      setActivationError(null);

      try {
        await adminAPI.setOrganizationActivation(data);
        await invalidateWithTimeout(queryClient, ["organizations"]);
        return true;
      } catch (error) {
        setActivationError(error as Error);
        return false;
      } finally {
        setIsTogglingActivation(false);
      }
    },
    [queryClient],
  );

  // Activate organization
  const activateOrganization = useCallback(
    (id: number) => toggleActivation({ id, is_active: true }),
    [toggleActivation],
  );

  // Deactivate organization
  const deactivateOrganization = useCallback(
    (id: number) => toggleActivation({ id, is_active: false }),
    [toggleActivation],
  );

  return {
    organizations,
    activeOrganizations,
    inactiveOrganizations,
    isLoading: query.isLoading,
    error: query.error ?? null,
    refetch: query.refetch,
    findById,
    findByTenantId,
    saveOrganization,
    activateOrganization,
    deactivateOrganization,
    isSaving,
    saveError,
    isTogglingActivation,
    activationError,
  };
}
